// Kategorien & Rotation: Kategorien-Karten, Rotationsboard, Codierung,
// Rotationsziele, Einstellungen (Scored Rotation, Klassifikationen).

import { useEffect, useMemo, useState } from "react";
import { api, fmtMs, fmtTime, toast, useLoad } from "../../api";
import { Empty, Field, HowTo, Modal, PageHead, Switch, Tip, useInstance, useMe } from "../../ui";
import { TimingEditor, timingText } from "../../schedule";

const ICONS: Record<string, string> = { music: "♪", star: "★", flame: "🔥", clock: "◷", heart: "♥", mic: "🎙", bolt: "⚡", disc: "◉", wave: "≈", bell: "🔔" };
const KINDS: [string, string][] = [["music", "Musik"], ["jingle", "Jingle"], ["dropper", "Dropper"], ["station_id", "Station-ID"], ["imaging", "Sweeper/Imaging"], ["ad", "Werbung"], ["news_bed", "Nachrichtenbett"], ["spoken", "Wort"], ["voicetrack", "Voicetrack"]];
const CADENCE: [string, string][] = [["lru", "Am längsten ungespielt zuerst"], ["weighted", "Gewichtet zufällig"], ["random", "Zufällig"], ["newest", "Neueste zuerst"]];

function useCanEdit() {
  const me = useMe();
  return ["admin", "programm"].includes(me?.role);
}

// ---------------------------------------------------------------- category editor

function RotationFields({ v, set }: { v: any; set: (p: any) => void }) {
  return (
    <>
      <Field label="Ruhezeit Song (min)" hint="so lange läuft derselbe Song nicht noch einmal"><input type="number" min={0} value={v.rest_min ?? ""} onChange={(e) => set({ rest_min: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
      <Field label="Ruhezeit Interpret (min)"><input type="number" min={0} value={v.artist_rest_min ?? ""} onChange={(e) => set({ artist_rest_min: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
      <Field label="Kadenz">
        <select value={v.cadence ?? "lru"} onChange={(e) => set({ cadence: e.target.value })}>{CADENCE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      </Field>
      <Field label="Reichweite (%)" hint="Anteil der Songs, die im Rennen sind"><input type="number" min={0} max={100} value={v.reach_pct ?? 100} onChange={(e) => set({ reach_pct: Number(e.target.value) })} /></Field>
      <Field label="Max. Spins pro Song und Stunde"><input type="number" min={0} value={v.max_spins_hour ?? ""} onChange={(e) => set({ max_spins_hour: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
      <Field label="Max. Spins pro Song und Tag"><input type="number" min={0} value={v.max_spins_day ?? ""} onChange={(e) => set({ max_spins_day: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
      <Field label="Scored Rotation" full><Switch checked={!!v.scored} onChange={(x) => set({ scored: x })} label={v.scored ? "Punktwertung (Ruhezeit × Gewicht, Ziele)" : "aus – Kadenz wie oben"} /></Field>
    </>
  );
}

/** Category settings – as a dialog, or inline as the "Rotation" tab of a folder in the library. */
export function CategoryEditor({ row, groups, onClose, inline, defaults }: { row: any | null; groups: any[]; onClose: (changed: boolean) => void; inline?: boolean; defaults?: any }) {
  const { data: folders } = useLoad<any[]>("/api/folders");
  const { data: cls } = useLoad<any[]>("/api/classifications");
  const { data: profiles } = useLoad<any>("/api/playback");
  const { data: roleDefs } = useLoad<any[]>("/api/role-defs");
  const inst = useInstance();
  const [v, setV] = useState<any>(row ?? { name: "", kind: "music", icon: "music", rule_mode: "off", rule_genres: [], rule_eras: [], rest_min: 180, artist_rest_min: 60, cadence: "lru", reach_pct: 100, ...defaults });
  useEffect(() => { if (row) setV(row); }, [row?.id]);
  const set = (p: any) => setV((x: any) => ({ ...x, ...p }));
  const [timing, setTiming] = useState(false);
  const save = async () => {
    const body: any = {};
    for (const k of ["name", "color", "icon", "kind", "group_id", "target_pct", "np_label", "rule_mode", "rule_genres", "rule_eras", "rest_min", "artist_rest_min", "reach_pct", "cadence", "max_spins_hour", "max_spins_day", "scored", "all_stations", "profile_id", "on_ramp", "ramp_duck_db", "ramp_min_intro_ms", "role", "gap_filler", "not_last", "source_folder_id", "source_folder_sub", "owner"]) if (k in v) body[k] = v[k] ?? (k === "role" ? "" : v[k]);
    if (row) for (const k of ["schedule", "valid_from", "valid_to"]) if (k in v) body[k] = v[k] ? (k === "schedule" ? v[k] : String(v[k]).slice(0, 10)) : null;
    try {
      if (row) await api(`/api/categories/${row.id}`, { method: "PUT", body });
      else await api("/api/categories", { body });
      toast(row ? `„${v.name}“ gespeichert` : `Ordner „${v.name}“ angelegt`);
      onClose(true);
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const eras = (cls ?? []).filter((c) => c.kind === "era").map((c) => c.value);
  const body = (
    <>
      <div className="form">
        <Field label="Name"><input type="text" value={v.name} onChange={(e) => set({ name: e.target.value })} /></Field>
        <Field label="Typ">
          <select value={v.kind} onChange={(e) => set({ kind: e.target.value })}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </Field>
        <Field label="Symbol">
          <div className="row wrap" style={{ gap: 4 }}>
            {Object.entries(ICONS).map(([k, s]) => <button key={k} type="button" className="btn small" style={{ background: v.icon === k ? "var(--accent-2)" : undefined }} onClick={() => set({ icon: k })}>{s}</button>)}
          </div>
        </Field>
        <Field label="Farbe"><input type="color" value={v.color ?? "#e8673c"} onChange={(e) => set({ color: e.target.value })} style={{ height: 34, width: 80 }} /></Field>
        <Field label="Gruppe">
          <select value={v.group_id ?? ""} onChange={(e) => set({ group_id: e.target.value || null })}>
            <option value="">–</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="Ziel-Anteil (%)" hint="Anteil an der Musik, bis die Uhren das übernehmen"><input type="number" min={0} max={100} value={v.target_pct ?? ""} onChange={(e) => set({ target_pct: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
        <Field label="Now-Playing-Label" full hint="leer = Vorlage aus Einstellungen › Metadaten; Platzhalter {interpret} {titel} {sender} {kategorie}"><input type="text" value={v.np_label ?? ""} onChange={(e) => set({ np_label: e.target.value })} /></Field>
        <Field label="Inhaltsregel beim Einsortieren">
          <select value={v.rule_mode} onChange={(e) => set({ rule_mode: e.target.value })}>
            <option value="off">Aus</option><option value="warn">Warnen</option><option value="block">Blockieren</option>
          </select>
        </Field>
        <Field label="Erlaubte Genres" hint="durch Komma getrennt">
          <input type="text" disabled={v.rule_mode === "off"} value={(v.rule_genres ?? []).join(", ")} onChange={(e) => set({ rule_genres: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
        </Field>
        <div className="field full">
          <span>Erlaubte Ären</span>
          <div className="row wrap">
            {eras.map((e) => (
              <label key={e} className="badge" style={{ cursor: "pointer", background: (v.rule_eras ?? []).includes(e) ? "var(--accent-2)" : undefined }}>
                <input type="checkbox" disabled={v.rule_mode === "off"} checked={(v.rule_eras ?? []).includes(e)} onChange={() => set({ rule_eras: (v.rule_eras ?? []).includes(e) ? v.rule_eras.filter((x: string) => x !== e) : [...(v.rule_eras ?? []), e] })} /> {e}
              </label>
            ))}
          </div>
        </div>
        <Field label="Abspielprofil" hint="eigenes Lautheitsziel (Einstellungen › Abspielen)">
          <select value={v.profile_id ?? ""} onChange={(e) => set({ profile_id: e.target.value || null })}>
            <option value="">– Sender-Standard –</option>{(profiles?.profiles ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Gehört zu" hint="Verbund/alle: gemeinsamer Ordner. Nur dieser Sender: z. B. eigene Jingles, Dropper oder Stundenopener – andere Sender sehen ihn nicht.">
          <select value={v.station_id ? "station" : "shared"} disabled={inst?.kind === "verbund"} onChange={(e) => set({ owner: e.target.value, station_id: e.target.value === "station" ? inst?.id : null })}>
            <option value="shared">{inst?.kind === "verbund" ? "Verbund (alle Sender)" : "Verbund / alle Sender"}</option>
            {inst?.kind !== "verbund" && <option value="station">nur {inst?.name}</option>}
          </select>
        </Field>
        <Field label="Rolle in den Uhren" hint="Uhrplätze mit dieser Rolle spielen beim jeweiligen Sender seinen Ordner dieser Rolle. Neue Rollen: Bibliothek › Rollen-Zuordnung.">
          <select value={v.role ?? ""} onChange={(e) => set({ role: e.target.value || null })}>
            <option value="">– keine –</option>
            {(roleDefs ?? []).map((r: any) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </Field>
        {v.kind !== "music" && (
          <Field label="Auf die Rampe des nächsten Songs legen" full hint="Jedes Element dieser Kategorie liegt über dem Intro des folgenden Songs und endet genau am Gesangseinsatz (wie bei mAirList). Ist das Intro zu kurz, läuft es davor.">
            <div className="row wrap">
              <Switch checked={!!v.on_ramp} onChange={(x) => set({ on_ramp: x })} label={v.on_ramp ? "an" : "aus"} />
              {v.on_ramp && <>
                <span className="small">Musik darunter leiser um</span><input type="number" min={0} max={20} step={0.5} style={{ width: 70 }} value={v.ramp_duck_db ?? 3} onChange={(e) => set({ ramp_duck_db: Number(e.target.value) })} /><span className="small">dB</span>
                <span className="small">Mindest-Intro</span><input type="number" min={0} max={20} step={0.5} style={{ width: 70 }} value={(v.ramp_min_intro_ms ?? 2000) / 1000} onChange={(e) => set({ ramp_min_intro_ms: Math.round(Number(e.target.value) * 1000) })} /><span className="small">s</span>
              </>}
            </div>
          </Field>
        )}
        {row && (
          <Field label="Sendezeiten" full hint="Wann diese Kategorie überhaupt laufen darf – z. B. Weihnachtssongs nur im Dezember, Party-Kategorie nur Fr/Sa abends.">
            <div className="row"><b>{timingText(v)}</b><button type="button" className="btn small" onClick={() => setTiming(true)}>Ändern</button></div>
          </Field>
        )}
        <Field label="Läuft auf" full><Switch checked={v.all_stations !== false} onChange={(x) => set({ all_stations: x })} label="allen Sendern" /></Field>
        <Field label="Stundenwechsel" full>
          <Switch checked={!!v.gap_filler} onChange={(x) => set({ gap_filler: x })} label="Darf als Lückenfüller vor dem Stundenwechsel laufen" />
          <Switch checked={!!v.not_last} onChange={(x) => set({ not_last: x })} label="Darf nicht das letzte Element der Stunde sein" />
        </Field>
        <Field label="Automatisch aus Ordner">
          <select value={v.source_folder_id ?? ""} onChange={(e) => set({ source_folder_id: e.target.value || null })}><option value="">Manuelle Zuordnung</option>{(folders ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</select>
          <Switch checked={v.source_folder_sub !== false} onChange={(x) => set({ source_folder_sub: x })} label="Mit Unterordnern" />
        </Field>
        <RotationFields v={v} set={set} />
      </div>
      {timing && <TimingEditor title={`Sendezeiten: ${v.name}`} value={v} onClose={() => setTiming(false)} onSave={(t) => { set(t); setTiming(false); }} />}
      <div className="row" style={{ marginTop: 16 }}>
        <span className="spacer" />
        {!inline && <button className="btn" onClick={() => onClose(false)}>Abbrechen</button>}
        <button className="btn primary" onClick={save}>{inline ? "Einstellungen speichern" : "Speichern"}</button>
      </div>
    </>
  );
  return inline ? <div className="card">{body}</div> : <Modal title={row ? `Kategorie „${row.name}“` : "Neuer Ordner (Kategorie)"} onClose={() => onClose(false)}>{body}</Modal>;
}

function DeleteDialog({ cat, cats, onClose }: { cat: any; cats: any[]; onClose: (changed: boolean) => void }) {
  const { data: impact } = useLoad<any>(`/api/categories/${cat.id}/impact`);
  const [moveTo, setMoveTo] = useState("");
  const del = async () => {
    await api(`/api/categories/${cat.id}${moveTo ? `?move_to=${moveTo}` : ""}`, { method: "DELETE" });
    toast(`Kategorie „${cat.name}“ gelöscht`);
    onClose(true);
  };
  return (
    <Modal title={`„${cat.name}“ löschen`} onClose={() => onClose(false)}>
      {!impact ? "Prüfe …" : (
        <div className="stack">
          <div>Betroffen: <b>{impact.songs}</b> Songs, davon <b>{impact.only_here}</b> in keiner anderen Kategorie.
            {impact.folders.length > 0 && <> Ordner mit Auto-Kategorie: {impact.folders.join(", ")}.</>}
            {impact.metadata && <> Die Kategorie ist in den Metadaten-Einstellungen ausgewählt.</>}
          </div>
          {impact.only_here > 0 && <div className="banner warn">{impact.only_here} Songs würden in keiner Kategorie mehr sein und nicht mehr gesendet.</div>}
          <Field label="Songs vorher verschieben nach">
            <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              <option value="">– nicht verschieben –</option>
              {cats.filter((c) => c.id !== cat.id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <div className="row"><span className="spacer" /><button className="btn" onClick={() => onClose(false)}>Abbrechen</button><button className="btn danger" onClick={del}>{moveTo ? "Songs verschieben, dann löschen" : "Löschen"}</button></div>
        </div>
      )}
    </Modal>
  );
}

function CategoryCards() {
  const canEdit = useCanEdit();
  const { data: cats, reload } = useLoad<any[]>("/api/categories");
  const { data: groups, reload: reloadGroups } = useLoad<any[]>("/api/category-groups");
  const [edit, setEdit] = useState<any | null | undefined>(undefined);
  const [del, setDel] = useState<any | null>(null);
  const [sel, setSel] = useState<string[]>([]);
  const [series, setSeries] = useState(false);
  const [seriesV, setSeriesV] = useState<any>({ rest_min: 180, artist_rest_min: 60, cadence: "lru", reach_pct: 100 });
  const totalPct = (cats ?? []).filter((c) => c.kind === "music").reduce((a, c) => a + (c.target_pct ?? 0), 0);
  const byGroup = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const c of cats ?? []) {
      const k = c.group_name ?? "";
      m.set(k, [...(m.get(k) ?? []), c]);
    }
    return [...m.entries()].sort((a, b) => (a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0], "de")));
  }, [cats]);
  return (
    <div className="stack">
      <div className="row wrap">
        {canEdit && <button className="btn primary" onClick={() => setEdit(null)}>+ Kategorie</button>}
        {canEdit && <button className="btn" onClick={async () => { await api("/api/categories/colors", { method: "POST" }); reload(); }}>Farben verteilen</button>}
        {canEdit && <button className="btn" onClick={async () => { const n = window.prompt("Name der neuen Gruppe"); if (n) { await api("/api/category-groups", { body: { name: n } }); reloadGroups(); } }}>+ Gruppe</button>}
        {canEdit && sel.length > 0 && <button className="btn" onClick={() => setSeries(true)}>Einstellungen auf {sel.length} Kategorien übertragen</button>}
        <span className="spacer" />
        <span className={`badge ${Math.abs(totalPct - 100) < 0.5 || totalPct === 0 ? "ok" : "warn"}`}>Ziel-Anteile Musik: {Math.round(totalPct)} %</span>
      </div>
      {!cats?.length && <Empty>Noch keine Kategorien. Lege die erste an – oder wandle in der Bibliothek einen Ordner um.</Empty>}
      {byGroup.map(([g, list]) => (
        <div key={g}>
          {g && <h3>{g}</h3>}
          <div className="grid g3">
            {list.map((c) => (
              <div key={c.id} className="card" style={{ borderTop: `4px solid ${c.color}` }}>
                <div className="row">
                  {canEdit && <input type="checkbox" checked={sel.includes(c.id)} onChange={() => setSel(sel.includes(c.id) ? sel.filter((x) => x !== c.id) : [...sel, c.id])} />}
                  <span style={{ color: c.color, fontSize: 18 }}>{ICONS[c.icon] ?? "♪"}</span>
                  <b>{c.name}</b>
                  <span className="badge">{KINDS.find(([k]) => k === c.kind)?.[1]}</span>
                  <span className="spacer" />
                  {c.target_pct ? <span className="badge accent">{c.target_pct} %</span> : null}
                </div>
                <div className="small muted" style={{ margin: "8px 0" }}>
                  {c.songs} Songs · {fmtMs(Number(c.total_ms))} · heute {c.plays_today}× gespielt
                </div>
                <div className="small">
                  Ruhezeit {c.rest_min} min · Interpret {c.artist_rest_min} min · {CADENCE.find(([k]) => k === c.cadence)?.[1]}
                  {c.scored && <span className="badge info" style={{ marginLeft: 4 }}>scored</span>}
                  {c.rule_mode !== "off" && <span className="badge warn" style={{ marginLeft: 4 }}>Regel: {c.rule_mode === "block" ? "blockiert" : "warnt"}</span>}
                </div>
                {c.songs === 0 && <div className="banner warn small" style={{ margin: "8px 0 0" }}>Leer – aus dieser Kategorie kann nichts gesendet werden.</div>}
                {canEdit && (
                  <div className="row" style={{ marginTop: 10 }}>
                    <a className="btn small" href={`#/plan/kategorien/board/${c.id}`}>Rotationsboard</a>
                    <span className="spacer" />
                    <button className="btn small" onClick={() => setEdit(c)}>Bearbeiten</button>
                    <button className="btn small danger" onClick={() => setDel(c)}>Löschen</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {edit !== undefined && <CategoryEditor row={edit} groups={groups ?? []} onClose={(c) => { setEdit(undefined); if (c) reload(); }} />}
      {del && <DeleteDialog cat={del} cats={cats ?? []} onClose={(c) => { setDel(null); if (c) reload(); }} />}
      {series && (
        <Modal title={`Rotations-Einstellungen für ${sel.length} Kategorien`} onClose={() => setSeries(false)}>
          <div className="form"><RotationFields v={seriesV} set={(p) => setSeriesV({ ...seriesV, ...p })} /></div>
          <div className="row" style={{ marginTop: 16 }}>
            <span className="spacer" />
            <button className="btn primary" onClick={async () => {
              try { await api("/api/categories/apply", { body: { ids: sel, settings: seriesV } }); toast("Übertragen"); setSeries(false); setSel([]); reload(); } catch (e: any) { toast(e.message, true); }
            }}>Übertragen</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- rotation board

function Board({ focus }: { focus?: string }) {
  const canEdit = useCanEdit();
  const { data: cats } = useLoad<any[]>("/api/categories");
  const [shown, setShown] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<{ cat: string; ids: string[] }>({ cat: "", ids: [] });
  const [addTo, setAddTo] = useState<string | null>(null);
  useEffect(() => {
    if (cats && !shown.length) setShown(focus ? [focus] : cats.filter((c) => c.kind === "music").slice(0, 4).map((c) => c.id));
  }, [cats, focus]);
  const { data: cols, reload } = useLoad<any[]>(shown.length ? `/api/rotation/board?categories=${shown.join(",")}&q=${encodeURIComponent(q)}` : null, [shown.join(","), q]);

  const move = async (ids: string[], from: string, to: string, copy: boolean) => {
    if (from === to && !copy) return;
    try {
      const r = await api("/api/rotation/move", { body: { media_ids: ids, from, to, copy } });
      const msg = [`${r.added} Songs ${copy ? "kopiert" : "verschoben"}`];
      if (r.blocked.length) msg.push(`${r.blocked.length} blockiert (Inhaltsregel)`);
      if (r.warnings.length) msg.push(`${r.warnings.length} Warnungen`);
      toast(msg.join(" · "), r.blocked.length > 0);
      setSel({ cat: "", ids: [] });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const member = async (cat: string, ids: string[], patch: any) => {
    try {
      await api("/api/rotation/member", { method: "PUT", body: { category_id: cat, media_ids: ids, ...patch } });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const remove = async (cat: string, ids: string[]) => {
    if (!window.confirm(`${ids.length} Songs aus der Kategorie entfernen? (Sie bleiben in der Bibliothek.)`)) return;
    await api("/api/rotation/remove", { body: { category_id: cat, media_ids: ids } });
    setSel({ cat: "", ids: [] });
    reload();
  };
  const toggleSel = (cat: string, id: string, multi: boolean) => {
    if (sel.cat !== cat || !multi) return setSel({ cat, ids: sel.cat === cat && sel.ids.length === 1 && sel.ids[0] === id ? [] : [id] });
    setSel({ cat, ids: sel.ids.includes(id) ? sel.ids.filter((x) => x !== id) : [...sel.ids, id] });
  };
  const scored = (cols ?? []).some((c) => c.scored);
  return (
    <div className="stack">
      <div className="row wrap">
        <input type="text" style={{ maxWidth: 240 }} placeholder="Song suchen" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="small muted">Spalten:</span>
        {(cats ?? []).map((c) => (
          <label key={c.id} className="badge" style={{ cursor: "pointer", background: shown.includes(c.id) ? `${c.color}33` : undefined }}>
            <input type="checkbox" checked={shown.includes(c.id)} onChange={() => setShown(shown.includes(c.id) ? shown.filter((x) => x !== c.id) : [...shown, c.id])} /> {c.name}
          </label>
        ))}
      </div>
      {scored && <div className="banner ok">Scored Rotation ist für mindestens eine Spalte an: Reihenfolge nach Punktwert (Ruhezeit × Gewicht, Ziele) statt reiner Kadenz.</div>}
      <Tip>Karten zwischen Spalten ziehen verschiebt den Song (mit gedrückter Alt-/Wahltaste: kopieren). Strg/⌘-Klick wählt mehrere Karten derselben Spalte.</Tip>
      {sel.ids.length > 0 && canEdit && (
        <div className="card row wrap" style={{ padding: "8px 12px", background: "var(--side)", color: "#fff", position: "sticky", top: 0, zIndex: 5 }}>
          <b>{sel.ids.length} ausgewählt</b>
          <select style={{ width: 140 }} value="" onChange={(e) => e.target.value && member(sel.cat, sel.ids, { weight: Number(e.target.value) })}>
            <option value="">Gewicht …</option>{[0.5, 1, 1.5, 2, 3, 5].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <select style={{ width: 160 }} value="" onChange={(e) => e.target.value && move(sel.ids, sel.cat, e.target.value, false)}>
            <option value="">Verschieben nach …</option>{(cats ?? []).filter((c) => c.id !== sel.cat).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={{ width: 160 }} value="" onChange={(e) => e.target.value && move(sel.ids, sel.cat, e.target.value, true)}>
            <option value="">Kopieren nach …</option>{(cats ?? []).filter((c) => c.id !== sel.cat).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn small" onClick={() => member(sel.cat, sel.ids, { active: true })}>Aktiv</button>
          <button className="btn small" onClick={() => member(sel.cat, sel.ids, { active: false })}>Pausieren</button>
          <span className="spacer" />
          <button className="btn small danger" onClick={() => remove(sel.cat, sel.ids)}>Entfernen</button>
        </div>
      )}
      <div style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: "minmax(260px, 1fr)", gap: 12, overflowX: "auto", alignItems: "start" }}>
        {(cols ?? []).map((c) => (
          <div key={c.id} className="card" style={{ padding: 10, borderTop: `4px solid ${c.color}`, minHeight: 200 }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
              if (d.ids?.length) move(d.ids, d.from, c.id, e.altKey);
            }}>
            <div className="row" style={{ marginBottom: 6 }}>
              <b>{c.name}</b><span className="muted small">{c.songs.length}</span>
              <span className="spacer" />
              {canEdit && <button className="btn small" onClick={() => setAddTo(c.id)}>+ Songs</button>}
            </div>
            <div className="small muted" style={{ marginBottom: 6 }}>Ruhezeit {c.rest_min} min · {CADENCE.find(([k]) => k === c.cadence)?.[1]}{c.max_spins_day ? ` · max ${c.max_spins_day}/Tag` : ""}</div>
            {!c.songs.length && <div className="muted small" style={{ padding: 12, textAlign: "center" }}>Songs hierher ziehen</div>}
            {c.songs.map((s: any) => {
              const isSel = sel.cat === c.id && sel.ids.includes(s.id);
              return (
                <div key={s.id} draggable={canEdit}
                  onDragStart={(e) => e.dataTransfer.setData("application/json", JSON.stringify({ ids: isSel ? sel.ids : [s.id], from: c.id }))}
                  onClick={(e) => toggleSel(c.id, s.id, e.ctrlKey || e.metaKey || e.shiftKey)}
                  style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "6px 8px", marginBottom: 6, background: isSel ? "var(--accent-2)" : s.active ? "#fff" : "#f1eee6", cursor: canEdit ? "grab" : "default", opacity: s.active ? 1 : 0.6 }}>
                  <div className="row" style={{ gap: 6 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="small" style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title}</div>
                      <div className="small muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.artist}</div>
                    </div>
                    <span className="small muted">{fmtMs(s.duration_ms)}</span>
                  </div>
                  <div className="row small muted" style={{ gap: 6, marginTop: 2 }}>
                    <span title="Gewicht">⚖ {s.weight}</span>
                    <span title="Spins letzte 7 Tage">{s.plays_7d}×/7T</span>
                    <span title="Zuletzt gespielt">{s.last_played ? fmtTime(s.last_played) : "nie"}</span>
                    {!s.active && <span className="badge">pausiert</span>}
                    {!s.released && <span className="badge warn">Freigabe</span>}
                    <span className="spacer" />
                    {canEdit && <button className="iconbtn small" title="Bearbeiten" onClick={(e) => { e.stopPropagation(); const w = window.prompt("Gewicht (0,1 – 10)", String(s.weight)); if (w) member(c.id, [s.id], { weight: Number(w.replace(",", ".")) }); }}>✎</button>}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {addTo && <AddSongs categoryId={addTo} onClose={() => { setAddTo(null); reload(); }} />}
    </div>
  );
}

function AddSongs({ categoryId, onClose }: { categoryId: string; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [sel, setSel] = useState<string[]>([]);
  useEffect(() => {
    const t = window.setTimeout(async () => {
      const r = await api(`/api/library?view=all&q=${encodeURIComponent(q)}`);
      setRows(r.items);
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);
  const add = async () => {
    const r = await api("/api/rotation/add", { body: { category_id: categoryId, media_ids: sel } });
    toast(`${r.added} hinzugefügt${r.blocked.length ? `, ${r.blocked.length} blockiert` : ""}${r.warnings.length ? `, ${r.warnings.length} Warnungen` : ""}`, r.blocked.length > 0);
    onClose();
  };
  return (
    <Modal title="Songs hinzufügen" onClose={onClose}>
      <input type="text" placeholder="Suche" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      <div style={{ maxHeight: 360, overflowY: "auto", marginTop: 8 }}>
        {rows.map((s) => (
          <label key={s.id} className="row" style={{ padding: "4px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={sel.includes(s.id)} onChange={() => setSel(sel.includes(s.id) ? sel.filter((x) => x !== s.id) : [...sel, s.id])} />
            <span>{s.artist ? `${s.artist} – ` : ""}{s.title}</span>
            <span className="muted small">{s.categories.map((c: any) => c.name).join(", ")}</span>
          </label>
        ))}
      </div>
      <div className="row" style={{ marginTop: 10 }}><span className="spacer" /><button className="btn primary" disabled={!sel.length} onClick={add}>{sel.length} hinzufügen</button></div>
    </Modal>
  );
}

// ---------------------------------------------------------------- coding

function Coding() {
  const { data: cats } = useLoad<any[]>("/api/categories");
  const { data: folders } = useLoad<any[]>("/api/folders");
  const { data: cls } = useLoad<any[]>("/api/classifications");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [uncoded, setUncoded] = useState(false);
  const [page, setPage] = useState(1);
  const qs = `category=${cat}&q=${encodeURIComponent(q)}&uncoded=${uncoded ? 1 : 0}&page=${page}`;
  const { data: rows, setData } = useLoad<any[]>(`/api/coding?${qs}`, [qs]);
  const save = async (id: string, patch: any) => {
    setData((rows ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      await api(`/api/media/${id}`, { method: "PUT", body: patch });
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const eras = (cls ?? []).filter((c) => c.kind === "era").map((c) => c.value);
  const scale = (r: any, k: string) => (
    <select value={r[k] ?? ""} onChange={(e) => save(r.id, { [k]: e.target.value ? Number(e.target.value) : null })} style={{ width: 56, padding: "3px 4px" }}>
      <option value="">–</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
  return (
    <div className="stack">
      <div className="row wrap">
        <select style={{ width: 200 }} value={cat} onChange={(e) => { setCat(e.target.value); setPage(1); }}>
          <option value="">Alle Musik</option>{(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input type="text" style={{ maxWidth: 220 }} placeholder="Suche" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        <Switch checked={uncoded} onChange={(x) => { setUncoded(x); setPage(1); }} label="nur unvollständig codierte" />
      </div>
      <Tip>Jede Änderung wird sofort gespeichert. Für viele Songs auf einmal: in der Bibliothek auswählen → „Codieren …“.</Tip>
      <div className="card" style={{ padding: 0, overflowX: "auto" }}>
        <table className="list">
          <thead><tr><th>Song</th><th>Stimmung</th><th>Energie</th><th>Tempo</th><th>Textur</th><th>Stimme</th><th>Ära</th><th>Genre</th><th>Ende</th></tr></thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr key={r.id}>
                <td><div style={{ fontWeight: 600 }}>{r.title}</div><div className="small muted">{r.artist}</div></td>
                <td>{scale(r, "mood")}</td><td>{scale(r, "energy")}</td><td>{scale(r, "tempo")}</td><td>{scale(r, "texture")}</td>
                <td>
                  <select value={r.voice_gender ?? ""} onChange={(e) => save(r.id, { voice_gender: e.target.value })} style={{ width: 90, padding: "3px 4px" }}>
                    <option value="">–</option><option value="m">männl.</option><option value="f">weibl.</option><option value="mix">gemischt</option><option value="inst">instr.</option>
                  </select>
                </td>
                <td>
                  <select value={r.era ?? ""} onChange={(e) => save(r.id, { era: e.target.value })} style={{ width: 90, padding: "3px 4px" }}>
                    <option value="">–</option>{eras.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </td>
                <td><input type="text" defaultValue={r.genre} onBlur={(e) => e.target.value !== r.genre && save(r.id, { genre: e.target.value })} style={{ width: 110, padding: "3px 6px" }} /></td>
                <td>
                  <select value={r.end_type ?? ""} onChange={(e) => save(r.id, { end_type: e.target.value })} style={{ width: 90, padding: "3px 4px" }}>
                    <option value="">–</option><option value="cold">kalt</option><option value="fade">Blende</option><option value="sustain">Ausklang</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows?.length && <Empty>Keine Songs.</Empty>}
      </div>
      <div className="row">
        <button className="btn small" disabled={page <= 1} onClick={() => setPage(page - 1)}>← zurück</button>
        <span className="small muted">Seite {page}</span>
        <button className="btn small" disabled={(rows?.length ?? 0) < 100} onClick={() => setPage(page + 1)}>weiter →</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- rotation targets

function Targets() {
  const canEdit = useCanEdit();
  const { data: cats } = useLoad<any[]>("/api/categories");
  const [cat, setCat] = useState("");
  const { data: rows, reload } = useLoad<any[]>(`/api/rotation/targets${cat ? `?category=${cat}` : ""}`, [cat]);
  const [ladder, setLadder] = useState(false);
  const [text, setText] = useState("");
  const [lcat, setLcat] = useState("");
  const [preview, setPreview] = useState<any[] | null>(null);
  const [timing, setTiming] = useState<any | null>(null);
  const setTarget = async (r: any, patch: any) => {
    try {
      await api("/api/rotation/member", { method: "PUT", body: { category_id: r.category_id, media_ids: [r.id], ...patch } });
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const runLadder = async (apply: boolean) => {
    try {
      const r = await api("/api/rotation/ladder", { body: { category_id: lcat, text, apply } });
      setPreview(r.rows);
      if (apply) {
        toast("Ziele übernommen");
        setLadder(false);
        reload();
      }
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <div className="stack">
      <div className="row wrap">
        <select style={{ width: 220 }} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Alle Kategorien</option>{(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="spacer" />
        {canEdit && <button className="btn" onClick={() => { setLadder(true); setPreview(null); setLcat(cat || (cats?.[0]?.id ?? "")); }}>Leiter einfügen …</button>}
      </div>
      <Tip>Ziel = gewünschte Spins pro Tag. „Ist“ ist der Schnitt der letzten 7 Tage. Mit „harte Obergrenze“ läuft der Song nach Erreichen des Ziels an diesem Tag nicht mehr. Das Stundenfenster beschränkt den Song auf eine Tageszeit; „Sendezeiten“ erlaubt Wochentage, einzelne Stunden und einen Zeitraum. „Uhren bauen aus Zielen“ kommt mit den Uhren in Stufe 4.</Tip>
      <div className="card" style={{ padding: 0 }}>
        <table className="list">
          <thead><tr><th>Kategorie</th><th>Song</th><th>Ziel/Tag</th><th>Ist (Ø 7 T)</th><th>Heute</th><th>Stunden</th><th>Sendezeiten</th><th>Harte Grenze</th></tr></thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr key={`${r.category_id}-${r.id}`}>
                <td className="small">{r.category}</td>
                <td><div style={{ fontWeight: 600 }}>{r.title}</div><div className="small muted">{r.artist}</div></td>
                <td><input type="number" min={0} step={0.5} disabled={!canEdit} defaultValue={r.target_per_day ?? ""} onBlur={(e) => setTarget(r, { target_per_day: e.target.value === "" ? null : Number(e.target.value) })} style={{ width: 70, padding: "3px 6px" }} /></td>
                <td style={{ color: r.target_per_day && Number(r.actual_per_day) < r.target_per_day * 0.8 ? "var(--warn)" : undefined }}>{r.actual_per_day}</td>
                <td>{r.today}</td>
                <td className="row" style={{ gap: 4 }}>
                  <input type="number" min={0} max={23} disabled={!canEdit} defaultValue={r.hour_from ?? ""} onBlur={(e) => setTarget(r, { hour_from: e.target.value })} style={{ width: 52, padding: "3px 4px" }} />–
                  <input type="number" min={1} max={24} disabled={!canEdit} defaultValue={r.hour_to ?? ""} onBlur={(e) => setTarget(r, { hour_to: e.target.value })} style={{ width: 52, padding: "3px 4px" }} />
                </td>
                <td className="small"><button className="btn small" disabled={!canEdit} onClick={() => setTiming(r)}>{timingText(r)}</button></td>
                <td><input type="checkbox" disabled={!canEdit} checked={!!r.hard_cap} onChange={(e) => setTarget(r, { hard_cap: e.target.checked })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows?.length && <Empty>Keine Songs in Kategorien.</Empty>}
      </div>
      {timing && <TimingEditor title={`Sendezeiten: ${timing.artist ? `${timing.artist} – ` : ""}${timing.title}`} value={timing} onClose={() => setTiming(null)}
        onSave={async (t) => { await setTarget(timing, t); setTiming(null); }} />}
      {ladder && (
        <Modal title="Leiter einfügen" onClose={() => setLadder(false)}>
          <p className="muted small" style={{ marginTop: 0 }}>Je Zeile „Interpret – Titel“ und am Ende das Ziel pro Tag (getrennt durch Tab, Semikolon oder Leerzeichen), z. B. aus einer Tabelle kopiert.</p>
          <Field label="Kategorie">
            <select value={lcat} onChange={(e) => setLcat(e.target.value)}>{(cats ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          </Field>
          <textarea rows={8} value={text} onChange={(e) => { setText(e.target.value); setPreview(null); }} placeholder={"Beispielband – Sommerlied\t4\nAndere Band – Herbstlied\t2"} style={{ marginTop: 8 }} />
          {preview && (
            <table className="list" style={{ marginTop: 8 }}>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i}>
                    <td className="small">{r.line}</td>
                    <td>{r.error ? <span className="badge bad">{r.error}</span> : <span className={`badge ${r.status === "neu" ? "info" : "ok"}`}>{r.status}{r.before != null ? ` (bisher ${r.before})` : ""}</span>}</td>
                    <td>{r.target}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="spacer" />
            <button className="btn" disabled={!text.trim() || !lcat} onClick={() => runLadder(false)}>Vorschau</button>
            <button className="btn primary" disabled={!preview || !preview.some((r) => r.id)} onClick={() => runLadder(true)}>Übernehmen</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- settings

function RotationSettings() {
  const canEdit = useCanEdit();
  const { data: s, reload } = useLoad<any>("/api/rotation/settings");
  const { data: cls, reload: reloadCls } = useLoad<any[]>("/api/classifications");
  const [kind, setKind] = useState("era");
  const [value, setValue] = useState("");
  if (!s) return null;
  const put = async (body: any) => {
    await api("/api/rotation/settings", { method: "PUT", body });
    reload();
  };
  return (
    <div className="grid g2">
      <div className="card">
        <h2>Rotation</h2>
        <div className="form">
          <Field label="Scored Rotation (Sender)" full hint="An: Punktwertung aus Ruhezeit, Gewicht und Zielen. Aus: am längsten ungespielt zuerst (bzw. Kadenz der Kategorie).">
            <Switch checked={s.scored} onChange={(x) => canEdit && put({ scored: x })} label={s.scored ? "an" : "aus"} />
          </Field>
          <Field label="Standard-Ruhezeit Song (min)"><input type="number" disabled={!canEdit} defaultValue={s.song_rest_min} onBlur={(e) => put({ song_rest_min: Number(e.target.value) })} /></Field>
          <Field label="Standard-Ruhezeit Interpret (min)"><input type="number" disabled={!canEdit} defaultValue={s.artist_rest_min} onBlur={(e) => put({ artist_rest_min: Number(e.target.value) })} /></Field>
        </div>
        <p className="small muted">Bis die Uhren (Stufe 4) stehen, wählt die Engine die Kategorie nach den Ziel-Anteilen der Kategorien; ohne Anteile reihum. Werden Regeln gelockert, steht das unter Verwaltung › Ereignisse.</p>
      </div>
      <div className="card">
        <h2>Klassifikationen</h2>
        <p className="small muted" style={{ marginTop: 0 }}>Zentral festgelegte Werte für Ära, Genre, Soundcode.</p>
        {["era", "genre", "soundcode"].map((k) => (
          <div key={k} style={{ marginBottom: 8 }}>
            <b className="small">{k === "era" ? "Ären" : k === "genre" ? "Genres" : "Soundcodes"}</b>
            <div className="row wrap" style={{ gap: 4, marginTop: 4 }}>
              {(cls ?? []).filter((c) => c.kind === k).map((c) => (
                <span key={c.id} className="badge">{c.value}{canEdit && <button className="iconbtn" style={{ padding: 0, marginLeft: 4 }} onClick={async () => { await api(`/api/classifications/${c.id}`, { method: "DELETE" }); reloadCls(); }}>✕</button>}</span>
              ))}
            </div>
          </div>
        ))}
        {canEdit && (
          <div className="row">
            <select style={{ width: 120 }} value={kind} onChange={(e) => setKind(e.target.value)}><option value="era">Ära</option><option value="genre">Genre</option><option value="soundcode">Soundcode</option></select>
            <input type="text" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Wert" />
            <button className="btn" disabled={!value.trim()} onClick={async () => { try { await api("/api/classifications", { body: { kind, value } }); setValue(""); reloadCls(); } catch (e: any) { toast(e.message, true); } }}>+</button>
          </div>
        )}
      </div>
    </div>
  );
}

const TABS: [string, string][] = [["karten", "Kategorien"], ["board", "Rotationsboard"], ["codierung", "Codierung"], ["ziele", "Rotationsziele"], ["einstellungen", "Einstellungen"]];

export { DeleteDialog };

export function CategoriesPage({ parts }: { parts: string[] }) {
  const tab = parts[0] ?? "karten";
  return (
    <div>
      <PageHead title="Rotation" sub={<a href="#/bibliothek">← zur Bibliothek</a>} />
      <div className="tabs">
        {TABS.map(([k, l]) => <a key={k} href={`#/plan/kategorien/${k}`} className={tab === k ? "active" : ""}>{l}</a>)}
      </div>
      <HowTo text="Kategorien bestimmen, was gesendet wird. Die Definition gilt für alle Sender, der Inhalt je Sender." chapter="kategorien" />
      {tab === "board" ? <Board focus={parts[1]} /> : tab === "codierung" ? <Coding /> : tab === "ziele" ? <Targets /> : tab === "einstellungen" ? <RotationSettings /> : <CategoryCards />}
    </div>
  );
}
